import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import UserCard from '../components/UserCard';

const API = API_URL.replace(/\/api$/, '');

export default function FollowingList({
  token,
  currentUserId,
  onShowProfile,
  onOpenConversation,
}: any) {
  const [following, setFollowing] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [listMode, setListMode] = useState<'grid' | 'list'>('list');
  const [statuses, setStatuses] = useState<Record<string, any>>({});
  const [search, setSearch] = useState(() => localStorage.getItem('auralink-following-search') || '');
  const [sort, setSort] = useState<'name' | 'recent'>('recent');

  useEffect(() => {
    if (!token || !currentUserId) return;
    axios
      .get(`${API}/api/users/${currentUserId}`, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((res) => setFollowing(res.data.following || []))
      .finally(() => setLoading(false));
  }, [token, currentUserId]);

  // Persist search
  useEffect(() => {
    try { localStorage.setItem('auralink-following-search', search); } catch {}
  }, [search]);

  // Load user statuses for harmonized subtitle
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const map: Record<string, any> = {};
        (res.data || []).forEach((s: any) => {
          if (s?.userId) map[String(s.userId)] = s;
        });
        setStatuses(map);
      })
      .catch(() => setStatuses({}));
  }, [token]);

  function avatarUrl(u: any) {
    if (!u?.avatar) return 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff';
    if (u.avatar.startsWith('http')) return u.avatar;
    if (u.avatar.startsWith('/')) return API + u.avatar;
    return API + '/uploads/' + u.avatar;
  }

  async function toggleFollow(u: any) {
    const route = u.isFollowed ? 'unfollow' : 'follow';

    await axios.post(
      `${API}/api/users/${u._id}/${route}`,
      {},
      { headers: { Authorization: 'Bearer ' + token } },
    );

    setFollowing((prev) =>
      prev.map((x) => (x._id === u._id ? { ...x, isFollowed: !u.isFollowed } : x)),
    );
  }

  const filtered = (following || []).filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [u.username, u.bio, u.sport, u.email].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'name') {
      return String(a.username || '').localeCompare(String(b.username || ''));
    }
    const sa = statuses[String(a._id)];
    const sb = statuses[String(b._id)];
    const ta = Date.parse(sa?.lastSeen || sa?.updatedAt || '1970-01-01');
    const tb = Date.parse(sb?.lastSeen || sb?.updatedAt || '1970-01-01');
    return tb - ta;
  });

  return (
    <div className="themed-page p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-heading flex items-center gap-2">
          <span>Following</span>
          <span className="badge">{sorted.length}</span>
        </h2>

        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="following-sort">Sort</label>
          <select
            id="following-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as any)}
            className="px-3 py-2 rounded-md border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
            title="Sort following"
          >
            <option value="recent">Recently active</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <label htmlFor="following-search" className="sr-only">Search following</label>
        <input
          id="following-search"
          type="text"
          placeholder="Search following by name, sport, bio, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSearch(''); } }}
          className="w-full pl-4 pr-12 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent themed-card"
        />
        {search && (
          <button
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setSearch('')}
            title="Clear"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        )}
        <div className="mt-2 text-xs text-theme-secondary">Showing {sorted.length} of {(following || []).length}</div>
      </div>

      {loading ? (
        <div className="text-theme-secondary">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-theme-secondary text-center py-12">
          <div className="text-4xl mb-3">➕</div>
          <div>Not following anyone yet.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((u) => (
            <UserCard
              key={u._id}
              user={u}
              mode="list"
              compact={true}
              isFollowingPage={true}
              userStatus={statuses[String(u._id)]}
              onShowProfile={onShowProfile}
              onOpenConversation={onOpenConversation}
              onToggleFollow={toggleFollow}
              avatarUrl={avatarUrl}
            />
          ))}
        </div>
      )}
    </div>
  );
}
